import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Heart, Loader2, AlertCircle, Check, MapPin, Clock, WifiOff,
  ChevronLeft, ListChecks, Send, Plus, BookOpen, ChevronDown,
} from 'lucide-react';
import { submitQuickLog, drainQueue, queueSize, startOfflineSync } from '../utils/outreachOfflineQueue';
import TranslatedPage from '../components/TranslatedPage';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtExpiry = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (d.getTime() - Date.now()) / 1000;
    if (diff <= 0) return 'expired';
    if (diff < 3600) return `${Math.floor(diff / 60)}m left`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h left`;
    return `${Math.floor(diff / 86400)}d left`;
  } catch { return ''; }
};

const SurveyInput = ({ q, value, onChange }) => {
  if (q.type === 'yes_no') {
    return (
      <div className="flex gap-2">
        {['Yes', 'No'].map((v) => (
          <button key={v} type="button"
            onClick={() => onChange(value === v ? '' : v)}
            className={`flex-1 py-2 rounded text-xs font-semibold border ${
              value === v ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
            }`}
            data-testid={`survey-${q.id}-${v.toLowerCase()}`}>
            {v}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'multi_choice') {
    // Multi-select: value is an array of selected options
    const selected = Array.isArray(value) ? value : (value ? [value] : []);
    const toggle = (opt) => {
      if (selected.includes(opt)) onChange(selected.filter((x) => x !== opt));
      else onChange([...selected, opt]);
    };
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`survey-${q.id}-multi`}>
        {(q.options || []).map((opt) => {
          const on = selected.includes(opt);
          return (
            <button key={opt} type="button"
              onClick={() => toggle(opt)}
              className={`px-2.5 py-1 rounded-full text-[11px] border inline-flex items-center gap-1 ${
                on ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
              }`}
              data-testid={`survey-${q.id}-opt-${opt}`}>
              {on ? <Check className="w-3 h-3" /> : null}
              {opt}
            </button>
          );
        })}
      </div>
    );
  }
  if (q.type === 'short_list') {
    // Single-select pills
    return (
      <div className="flex flex-wrap gap-1.5">
        {(q.options || []).map((opt) => (
          <button key={opt} type="button"
            onClick={() => onChange(value === opt ? '' : opt)}
            className={`px-2.5 py-1 rounded-full text-[11px] border ${
              value === opt ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
            }`}
            data-testid={`survey-${q.id}-opt-${opt}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  // text
  return (
    <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2}
      placeholder="(optional)"
      className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none resize-none"
      data-testid={`survey-${q.id}-text`} />
  );
};

/* Mobile-friendly accordion volunteer guide */
const VolunteerGuidePanel = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  const loadGuide = async () => {
    if (sections !== null || loading) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/outreach/guide/${encodeURIComponent(token)}`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSections(data.sections || []);
    } catch (e) {
      setError(e.message || 'Could not load the guide.');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadGuide();
  };

  return (
    <div className="border border-[#E5E7EB] rounded bg-white" data-testid="volunteer-guide-panel">
      <button type="button" onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-[#0A0A0A]"
        data-testid="volunteer-guide-toggle">
        <span className="inline-flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5 text-[#BA1826]" />
          Volunteer Guide
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-[#E5E7EB] p-3 space-y-2" data-testid="volunteer-guide-content">
          {loading && <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#BA1826]" /></div>}
          {error && <p className="text-[11px] text-red-700">{error}</p>}
          {!loading && !error && sections && sections.length === 0 && (
            <p className="text-[11px] text-[#9CA3AF] italic">No guide content has been set up yet. Reach out to your NHF lead.</p>
          )}
          {!loading && sections && sections.map((s, i) => {
            const isOpen = expanded[s.id || i];
            return (
              <div key={s.id || i} className="border border-[#E5E7EB] rounded" data-testid={`volunteer-guide-section-${i}`}>
                <button type="button"
                  onClick={() => setExpanded((prev) => ({ ...prev, [s.id || i]: !isOpen }))}
                  className="w-full flex items-center justify-between px-2.5 py-2 text-left"
                  data-testid={`volunteer-guide-section-toggle-${i}`}>
                  <span className="text-xs font-bold text-[#0A0A0A]">{s.title}</span>
                  <ChevronDown className={`w-3 h-3 text-[#9CA3AF] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <p className="px-2.5 pb-2 text-[11px] text-[#4B5563] whitespace-pre-wrap leading-relaxed">{s.body}</p>
                )}
              </div>
            );
          })}
          <p className="text-[10px] text-[#9CA3AF] italic pt-1">
            Keep it neighborly. Conversation first. Never push for a donation.
          </p>
        </div>
      )}
    </div>
  );
};

const PublicOutreachQuickLog = () => {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [savedLogs, setSavedLogs] = useState([]); // {id, when, neighbor, queued}
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [queued, setQueued] = useState(queueSize());

  const [interaction, setInteraction] = useState('conversation');
  const [streetId, setStreetId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [neighborName, setNeighborName] = useState('');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [survey, setSurvey] = useState({});
  const [showSurvey, setShowSurvey] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedToast, setSavedToast] = useState('');

  // Load context
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/outreach/quick-log/${encodeURIComponent(token)}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setCtx(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this volunteer link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Wire offline sync + connectivity status
  useEffect(() => {
    startOfflineSync();
    const onOn = () => { setOnline(true); drainQueue().then((r) => setQueued(queueSize())); };
    const onOff = () => setOnline(false);
    window.addEventListener('online', onOn);
    window.addEventListener('offline', onOff);
    const iv = setInterval(() => setQueued(queueSize()), 5_000);
    return () => {
      window.removeEventListener('online', onOn);
      window.removeEventListener('offline', onOff);
      clearInterval(iv);
    };
  }, []);

  const resetForm = useCallback(() => {
    setInteraction('conversation');
    setStreetId('');
    setSegmentId('');
    setNeighborName('');
    setNotes('');
    setFollowUp(false);
    setSurvey({});
    setShowSurvey(false);
  }, []);

  const submit = async () => {
    if (!ctx || !interaction) return;
    setSubmitting(true); setSavedToast('');
    const cleanedSurvey = {};
    for (const [k, v] of Object.entries(survey)) {
      if (v === '' || v === null || v === undefined) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      cleanedSurvey[k] = v;
    }
    const payload = {
      interaction_type: interaction,
      street_id: streetId || null,
      segment_id: segmentId || null,
      neighbor_name: neighborName.trim() || null,
      notes: notes.trim() || null,
      survey_responses: Object.keys(cleanedSurvey).length ? cleanedSurvey : null,
      follow_up_needed: !!followUp,
    };
    const r = await submitQuickLog({ backendUrl: BACKEND_URL, token, payload });
    setQueued(queueSize());
    if (r.ok) {
      setSavedLogs((prev) => [{
        id: r.id || r.client_id,
        when: new Date().toISOString(),
        neighbor: neighborName || '—',
        interaction,
        queued: !!r.queued,
      }, ...prev].slice(0, 25));
      setSavedToast(r.queued ? 'Saved offline — will sync when back online.' : 'Log saved. Thank you.');
      resetForm();
      setTimeout(() => setSavedToast(''), 3500);
    } else {
      setError(r.error || 'Could not save log');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  if (error && !ctx) {
    return (
      <TranslatedPage context="volunteer_quick_log_error">
        <div className="min-h-screen bg-[#FAFBFC] flex flex-col">
          <div className="bg-white border-b border-[#E5E7EB] px-4 py-2 flex justify-end">
            <LanguageSelector />
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="bg-white border border-red-200 rounded-lg p-6 max-w-sm text-center" data-testid="quick-log-error">
              <AlertCircle className="w-10 h-10 text-[#BA1826] mx-auto mb-3" />
              <h2 className="font-heading text-base font-bold text-[#0A0A0A]">This volunteer link can&apos;t be used right now.</h2>
              <p className="text-xs text-[#4B5563] mt-2">{error}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-3">
                Please ask the admin who shared this QR for a fresh link.
              </p>
            </div>
          </div>
        </div>
      </TranslatedPage>
    );
  }

  const zone = ctx.zone;
  const survey_schema = ctx.survey?.schema || [];

  return (
    <TranslatedPage context="volunteer_quick_log">
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="public-quick-log-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <span className="w-3 h-3 rounded-full" style={{ background: zone.color_tag || '#BA1826' }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#0A0A0A] truncate" data-no-translate="true">{zone.name}</p>
            <p className="text-[10px] text-[#9CA3AF] truncate flex items-center gap-1" data-no-translate="true">
              <MapPin className="w-3 h-3" />
              {[zone.neighborhood, zone.city, zone.state].filter(Boolean).join(' · ') || 'No location set'}
            </p>
          </div>
          <LanguageSelector />
          <div className="text-right">
            <p className="text-[10px] text-[#4B5563] flex items-center gap-1 justify-end">
              <Clock className="w-3 h-3" /> {fmtExpiry(ctx.expires_at)}
            </p>
            {!online && (
              <p className="text-[10px] text-amber-700 font-bold flex items-center gap-1 justify-end" data-testid="offline-banner">
                <WifiOff className="w-3 h-3" /> Offline
              </p>
            )}
            {online && queued > 0 && (
              <p className="text-[10px] text-amber-700 font-bold" data-testid="queued-banner">
                Syncing {queued} pending…
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Volunteer crew label */}
        <div className="bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg p-3 text-center">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#BA1826]">Logging as</p>
          <p className="text-sm font-bold text-[#0A0A0A]">{ctx.volunteer_label}</p>
          <p className="text-[10px] text-[#4B5563] mt-0.5">
            Every conversation matters. Thank you for being out there.
          </p>
        </div>

        {/* Volunteer Guide accordion (mobile-friendly cheat sheet) */}
        <VolunteerGuidePanel token={token} />

        {savedToast && (
          <div className="bg-green-50 border border-green-200 text-green-800 text-xs p-2 rounded flex items-center gap-2"
            data-testid="quick-log-toast">
            <Check className="w-3 h-3" /> {savedToast}
          </div>
        )}

        {/* Interaction type */}
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1.5">What happened?</p>
          <div className="grid grid-cols-2 gap-1.5" data-testid="quick-itypes">
            {(ctx.interaction_types || []).map((t) => {
              const active = interaction === t;
              return (
                <button key={t} type="button"
                  onClick={() => setInteraction(t)}
                  className={`text-[11px] py-2 px-2 rounded border text-left ${
                    active ? 'bg-[#BA1826] text-white border-[#BA1826] font-semibold' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
                  }`}
                  data-testid={`quick-itype-${t}`}>
                  {ctx.interaction_labels[t] || t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Street */}
        {ctx.streets && ctx.streets.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Street (optional)</p>
            <select value={streetId} onChange={(e) => { setStreetId(e.target.value); setSegmentId(''); }}
              className="w-full px-2.5 py-2 border border-[#E5E7EB] rounded text-sm"
              data-testid="quick-street-select">
              <option value="">— No specific street —</option>
              {ctx.streets.map((s) => <option key={s.id} value={s.id}>{s.name}{s.is_pilot ? '  ★ pilot' : ''}</option>)}
            </select>
          </div>
        )}

        {/* Segments — shown only after picking a street that has any */}
        {streetId && ctx.segments && ctx.segments.filter(sg => sg.street_id === streetId).length > 0 && (
          <div data-testid="quick-segments-block">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Segment / block (optional)</p>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={() => setSegmentId('')}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${
                  !segmentId ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
                }`}
                data-testid="quick-segment-none">
                Any
              </button>
              {ctx.segments.filter(sg => sg.street_id === streetId).map((sg) => {
                const range = sg.range_start && sg.range_end
                  ? ` ${sg.range_start} → ${sg.range_end}`
                  : sg.range_start ? ` from ${sg.range_start}` : '';
                return (
                  <button key={sg.id} type="button"
                    onClick={() => setSegmentId(segmentId === sg.id ? '' : sg.id)}
                    className={`px-2.5 py-1 rounded-full text-[11px] border ${
                      segmentId === sg.id ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
                    }`}
                    data-testid={`quick-segment-${sg.id}`}>
                    {sg.label}{range}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Neighbor name */}
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Neighbor&apos;s name (optional)</p>
          <input value={neighborName} onChange={(e) => setNeighborName(e.target.value)}
            placeholder="Leave blank if they preferred not to share"
            className="w-full px-2.5 py-2 border border-[#E5E7EB] rounded text-sm"
            data-testid="quick-neighbor-name" />
        </div>

        {/* Notes */}
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Notes</p>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="What did you talk about? Anything to remember for next time?"
            className="w-full px-2.5 py-2 border border-[#E5E7EB] rounded text-sm resize-none"
            data-testid="quick-notes" />
        </div>

        {/* Follow up */}
        <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0A0A0A]">
          <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)}
            data-testid="quick-followup" />
          <span>This neighbor wants us to come back</span>
        </label>

        {/* Survey (collapsed by default to keep the flow fast) */}
        {survey_schema.length > 0 && (
          <div className="border border-[#E5E7EB] rounded">
            <button type="button" onClick={() => setShowSurvey(s => !s)}
              className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-[#0A0A0A]"
              data-testid="quick-survey-toggle">
              <span className="inline-flex items-center gap-1.5">
                <ListChecks className="w-3 h-3 text-[#BA1826]" />
                Short survey ({survey_schema.length} questions)
              </span>
              <Plus className={`w-3 h-3 transition-transform ${showSurvey ? 'rotate-45' : ''}`} />
            </button>
            {showSurvey && (
              <div className="p-3 border-t border-[#E5E7EB] space-y-3">
                {survey_schema.map((q) => (
                  <div key={q.id} data-testid={`survey-q-${q.id}`}>
                    <p className="text-xs font-semibold text-[#0A0A0A] mb-1">{q.label}</p>
                    <SurveyInput q={q} value={survey[q.id] || ''}
                      onChange={(v) => setSurvey((s) => ({ ...s, [q.id]: v }))} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Submit */}
        <button onClick={submit} disabled={submitting || !interaction}
          className="w-full bg-[#BA1826] text-white py-3 rounded font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="quick-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> :
            (online ? <Send className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />)}
          {online ? 'Save log' : 'Save offline'}
        </button>

        {/* Today's saved logs (this session) */}
        {savedLogs.length > 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded p-3" data-testid="quick-session-list">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-2">
              This session: {savedLogs.length} log{savedLogs.length === 1 ? '' : 's'}
            </p>
            <ul className="text-xs space-y-1">
              {savedLogs.map((l, i) => (
                <li key={i} className="flex items-center justify-between border-b border-[#F1F5F9] py-1 last:border-0">
                  <span>
                    <span className="text-[#0A0A0A]">{l.neighbor}</span>
                    <span className="text-[#9CA3AF]"> · {l.interaction.replace(/_/g, ' ')}</span>
                  </span>
                  {l.queued && <span className="text-[10px] text-amber-700">queued</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-center text-[10px] text-[#9CA3AF] pt-4">
          Novena Humanitarian Foundation · Volunteer mode
          <br />
          <a href="/" className="inline-flex items-center gap-1 mt-1 text-[#4B5563]">
            <ChevronLeft className="w-3 h-3" /> back to site
          </a>
        </p>
      </div>
    </div>
    </TranslatedPage>
  );
};

export default PublicOutreachQuickLog;
